import { Dialog } from "@kobalte/core/dialog";
import { createChatClient } from "@storytell/client";
import type { StreamingChatId } from "@storytell/streaming";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { EmailTagsInput } from "@storytell/ui/email-tags-input";
import { useNativeTooltipTitle } from "@storytell/ui/tooltip";
import { TbCheck, TbCopy, TbInfoCircle, TbLink, TbLinkOff, TbLoader2, TbMail, TbX } from "solid-icons/tb";
import { type Component, createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import { WhenTooltipsAllowed } from "~/components/ui/WhenTooltipsAllowed";
import { useI18n } from "~/i18n/context";
import { getAppBaseUrl } from "~/lib/getAppBaseUrl";
import { trackFeature } from "~/lib/trackFeature";
import { copyHtmlToClipboard, copyTextToClipboard } from "~/utils/copyRichText";
import { getLastAnswerOutline, type OutlineHeading } from "~/utils/headingAnchorUtils";
import { useWire } from "~/wire";
import { buildShareOutline, buildSharePath, outlineDepths } from "./buildSharePath";

interface ShareChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
  chatTitle: string;
  textFragment?: string;
  /** Plain text to scroll to; added as query param so shared page can scroll (browser strips #:~:text= before JS runs) */
  scrollToText?: string;
  /** Message id to scroll to by element instead of text anchor; takes priority over `scrollToText`. */
  scrollToMessageId?: string;
  /** Display-only text for the "Share text block" section. Does not affect the shared URL. Falls back to `scrollToText` when not set. */
  textBlock?: string;
  /** Heading a section link points at; added to the URL so its link preview names the section. */
  sectionTitle?: string;
  autoCreateLink?: boolean;
  /** When sharing from a prompt, URL that starts a new chat with that prompt; shown as extra section in modal */
  promptShareUrl?: string;
}

const MAX_EMAILS = 10;

const shareBaseForToken = (token: string) => `${getAppBaseUrl()}/shared/chat/${token}`;

export const ShareChatModal: Component<ShareChatModalProps> = (props) => {
  const { t } = useI18n();
  const wire = useWire();
  const nativeTitle = useNativeTooltipTitle();

  const [isLoading, setIsLoading] = createSignal(true);
  const [isShared, setIsShared] = createSignal(false);
  const [shareToken, setShareToken] = createSignal<string | null>(null);
  const [copied, setCopied] = createSignal(false);
  const [promptLinkCopied, setPromptLinkCopied] = createSignal(false);
  const [textBlockCopied, setTextBlockCopied] = createSignal(false);
  const [outlineCopied, setOutlineCopied] = createSignal(false);
  const [outlineHeadings, setOutlineHeadings] = createSignal<OutlineHeading[]>([]);
  let copiedTimer: ReturnType<typeof setTimeout> | undefined;
  let promptLinkCopiedTimer: ReturnType<typeof setTimeout> | undefined;
  let textBlockCopiedTimer: ReturnType<typeof setTimeout> | undefined;
  let outlineCopiedTimer: ReturnType<typeof setTimeout> | undefined;
  const scheduleCopiedReset = () => {
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => setCopied(false), 2000);
  };
  const schedulePromptLinkCopiedReset = () => {
    clearTimeout(promptLinkCopiedTimer);
    promptLinkCopiedTimer = setTimeout(() => setPromptLinkCopied(false), 2000);
  };
  const scheduleTextBlockCopiedReset = () => {
    clearTimeout(textBlockCopiedTimer);
    textBlockCopiedTimer = setTimeout(() => setTextBlockCopied(false), 2000);
  };
  const scheduleOutlineCopiedReset = () => {
    clearTimeout(outlineCopiedTimer);
    outlineCopiedTimer = setTimeout(() => setOutlineCopied(false), 2000);
  };
  onCleanup(() => {
    clearTimeout(copiedTimer);
    clearTimeout(promptLinkCopiedTimer);
    clearTimeout(textBlockCopiedTimer);
    clearTimeout(outlineCopiedTimer);
  });

  const [error, setError] = createSignal<string | null>(null);
  const [isCreating, setIsCreating] = createSignal(false);
  const [isRevoking, setIsRevoking] = createSignal(false);
  // Track if auto-create has been attempted to prevent retry loops
  const [autoCreateAttempted, setAutoCreateAttempted] = createSignal(false);

  const [showEmailSection, setShowEmailSection] = createSignal(false);
  const [emails, setEmails] = createSignal<string[]>([]);
  const [isSendingEmails, setIsSendingEmails] = createSignal(false);
  const [emailSuccess, setEmailSuccess] = createSignal<string | null>(null);
  const [emailError, setEmailError] = createSignal<string | null>(null);

  const buildShareUrlForToken = (token: string) => {
    const base = shareBaseForToken(token);
    const leaf = wire.chat.getActiveLeafMessageId(props.chatId as StreamingChatId);
    const { path, usedTextAnchors } = buildSharePath(base, {
      scrollToText: props.scrollToText,
      scrollToMessageId: props.scrollToMessageId,
      section: props.sectionTitle,
      leaf,
    });
    const hash = usedTextAnchors ? "" : (props.textFragment ?? "");
    return `${path}${hash}`;
  };

  const getShareUrl = () => {
    const token = shareToken();
    if (!token) return "";
    return buildShareUrlForToken(token);
  };

  const loadShareStatus = async () => {
    if (!props.chatId) return;

    setIsLoading(true);
    setError(null);

    try {
      const chatClient = createChatClient(wire.auth.token());
      const status = await chatClient.getShareStatus(props.chatId);
      setIsShared(status.isShared);
      if (status.token) {
        setShareToken(status.token);
      }

      // If link already exists, auto-copy to clipboard and show the copied confirmation in the modal
      if (status.isShared && status.token) {
        const url = buildShareUrlForToken(status.token);
        const ok = await copyTextToClipboard(url);
        if (ok) {
          setCopied(true);
          scheduleCopiedReset();
        }
      }
    } catch {
      setError(t("chat_share_error_load"));
    } finally {
      setIsLoading(false);
    }
  };

  // Load status when modal opens
  createEffect(() => {
    if (props.isOpen && props.chatId) {
      void loadShareStatus();
      setAutoCreateAttempted(false);
      setShowEmailSection(false);
      setEmails([]);
      setEmailSuccess(null);
      setEmailError(null);
    }
  });

  // Kept out of the load effect: the chat-store reads re-run their effect whenever the streaming
  // flag flips, which there would reload the share status and reset the email form.
  createEffect(() => {
    if (!props.isOpen || !props.chatId) return;
    // The outline is read from the rendered thread, so it only describes the chat on screen —
    // sharing another chat from the list would otherwise put its headings behind this token.
    const isChatOnScreen = wire.chat.getChatId() === props.chatId;
    const streamingMessageId = wire.chat.isChatStreaming()
      ? wire.chat.getActiveLeafMessageId(props.chatId as StreamingChatId)
      : undefined;
    setOutlineHeadings(isChatOnScreen ? getLastAnswerOutline(streamingMessageId) : []);
  });

  // Auto-create link if requested (only once per modal open)
  createEffect(() => {
    if (
      props.isOpen &&
      props.autoCreateLink &&
      !isShared() &&
      !isLoading() &&
      !isCreating() &&
      !autoCreateAttempted()
    ) {
      setAutoCreateAttempted(true);
      void handleCreateLink();
    }
  });

  const handleCreateLink = async () => {
    if (isCreating()) return;
    setIsCreating(true);
    setError(null);

    try {
      const chatClient = createChatClient(wire.auth.token());
      const result = await chatClient.createShareLink(props.chatId);
      trackFeature({ feature: "chat.share", action: "link", via: "button" });
      setIsShared(true);
      if (result.token) {
        setShareToken(result.token);
        const url = buildShareUrlForToken(result.token);
        const ok = await copyTextToClipboard(url);
        if (ok) {
          setCopied(true);
          scheduleCopiedReset();
        }
      }
    } catch (err) {
      // If the error is our "link already exists" application code, refresh status instead of an error.
      // Do not key off HTTP 409 alone: other failures (e.g. Aborted) can map to 409 in errcodes.
      const errWithMeta = err as Error & { code?: string };
      const isAlreadyExists =
        errWithMeta?.code === "chats.public_sharing.create.exists" ||
        (err instanceof Error ? err.message : String(err)).includes("chats.public_sharing.create.exists") ||
        (err instanceof Error ? err.message : String(err)).includes("chats.public_sharing.create.exec");
      if (isAlreadyExists) {
        await loadShareStatus();
      } else {
        setError(t("chat_share_error_create"));
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeLink = async () => {
    setIsRevoking(true);
    setError(null);

    try {
      const chatClient = createChatClient(wire.auth.token());
      await chatClient.revokeShareLink(props.chatId);
      trackFeature({ feature: "chat.share", action: "revoke", via: "button" });
      setIsShared(false);
      setShareToken(null);
      setShowEmailSection(false);
    } catch {
      setError(t("chat_share_error_revoke"));
    } finally {
      setIsRevoking(false);
    }
  };

  const handleCopyLink = async () => {
    const url = getShareUrl();
    if (!url) return;

    const ok = await copyTextToClipboard(url);
    if (ok) {
      setCopied(true);
      scheduleCopiedReset();
    }
  };

  const handleCopyPromptLink = async () => {
    const url = props.promptShareUrl;
    if (!url) return;

    trackFeature({ feature: "prompt.share_url", via: "button" });
    const ok = await copyTextToClipboard(url);
    if (ok) {
      setPromptLinkCopied(true);
      schedulePromptLinkCopiedReset();
    }
  };

  // Indented by the depth the copied outline nests them at, so the preview matches the paste.
  const previewRows = () => {
    const headings = outlineHeadings();
    const depths = outlineDepths(headings);
    return headings.map((heading, index) => ({ text: heading.text, depth: depths[index] }));
  };

  const displayedTextBlock = () => props.textBlock?.trim() || props.scrollToText?.trim() || "";

  const handleCopyTextBlock = async () => {
    const url = getShareUrl();
    const text = displayedTextBlock();
    if (!url || !text) return;

    try {
      const html = `<a href="${url.replace(/"/g, "&quot;")}">${text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</a>`;
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      setTextBlockCopied(true);
      scheduleTextBlockCopiedReset();
    } catch {
      const ok = await copyTextToClipboard(text);
      if (ok) {
        setTextBlockCopied(true);
        scheduleTextBlockCopiedReset();
      }
    }
  };

  const handleCopyOutline = async () => {
    const token = shareToken();
    const headings = outlineHeadings();
    if (!token || headings.length === 0) return;

    const base = shareBaseForToken(token);
    const leaf = wire.chat.getActiveLeafMessageId(props.chatId as StreamingChatId);
    const { html, plainText } = buildShareOutline({
      base,
      topUrl: buildSharePath(base, { leaf }).path,
      title: props.chatTitle,
      headings,
      leaf,
    });
    const ok = (await copyHtmlToClipboard(html, plainText)) || (await copyTextToClipboard(plainText));
    if (ok) {
      setOutlineCopied(true);
      scheduleOutlineCopiedReset();
    }
  };

  const handleSendEmails = async () => {
    const emailList = emails();
    if (emailList.length === 0) return;

    setIsSendingEmails(true);
    setEmailError(null);
    setEmailSuccess(null);

    try {
      const chatClient = createChatClient(wire.auth.token());
      const result = await chatClient.sendShareEmails(props.chatId, emailList, props.textFragment);
      trackFeature({ feature: "chat.share", action: "email", via: "button" });

      if (result.sent > 0) {
        if (result.sent === 1) {
          setEmailSuccess(t("chat_share_email_queued"));
        } else {
          setEmailSuccess(t("chat_share_email_queued_plural", { count: String(result.sent) }));
        }
        setEmails([]);
      }

      if (result.failed && result.failed.length > 0) {
        const failedEmails = result.failed.map((f) => f.email).join(", ");
        setEmailError(`${t("chat_share_email_invalid")}: ${failedEmails}`);
      }
    } catch {
      setEmailError(t("chat_share_email_error"));
    } finally {
      setIsSendingEmails(false);
    }
  };

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-90 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-90 flex items-center justify-center">
              <Dialog.Content class="relative max-h-[80vh] w-screen max-w-md overflow-y-auto rounded-xl bg-panel p-6 text-primary-text">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4">
                  <TbX />
                </Dialog.CloseButton>

                <div class="mb-6">
                  <h3 class="mb-2 text-lg font-medium">{t("chat_share_modal_title")}</h3>
                  <p class="truncate text-sm text-secondary-text" title={nativeTitle(props.chatTitle)}>
                    {props.chatTitle}
                  </p>
                  <Show when={props.textFragment}>
                    <p class="mt-1 text-xs text-secondary-text">{t("chat_share_scrolls_to_answer")}</p>
                  </Show>
                </div>

                <Show when={isLoading()}>
                  <div class="flex items-center justify-center py-8">
                    <TbLoader2 class="size-6 animate-spin text-secondary-text" />
                  </div>
                </Show>

                <Show when={!isLoading()}>
                  <Show when={isShared()}>
                    <div class="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/20">
                      <TbInfoCircle class="mt-0.5 size-5 shrink-0 text-blue-600 dark:text-blue-400" />
                      <p class="text-sm text-blue-800 dark:text-blue-200">
                        {t("chat_share_warning")}{" "}
                        <WhenTooltipsAllowed>
                          <a
                            href="https://go.chief.bot/learn-secret-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="font-medium underline hover:no-underline"
                          >
                            {t("chat_share_learn_more")}
                          </a>
                        </WhenTooltipsAllowed>
                      </p>
                    </div>
                  </Show>

                  <Show when={error()}>
                    <div class="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/20">
                      <p class="text-sm text-red-800 dark:text-red-200">{error()}</p>
                    </div>
                  </Show>

                  <Show when={!isShared()}>
                    <div class="flex flex-col items-center py-4">
                      <TbLink class="mb-4 size-12 text-secondary-text" />
                      <p class="mb-4 text-center text-sm text-secondary-text">{t("chat_share_not_shared")}</p>
                      <StButton
                        onClick={() => {
                          handleCreateLink().catch((cause: unknown) =>
                            console.error("Failed to create share link", cause)
                          );
                        }}
                        disabled={isCreating()}
                        class="rounded-lg bg-brand px-4 py-2 text-on-brand hover:bg-brand-hover disabled:opacity-50"
                      >
                        <Show when={isCreating()} fallback={<>{t("chat_share_create_link")}</>}>
                          <TbLoader2 class="mr-2 size-4 animate-spin" />
                          {t("chat_share_creating")}
                        </Show>
                      </StButton>
                    </div>
                  </Show>

                  <Show when={isShared()}>
                    <div class="space-y-4">
                      <p class="text-sm font-bold text-primary-text">{t("chat_share_secret_link")}</p>
                      <div class="flex items-center gap-2">
                        <input
                          type="text"
                          readonly
                          value={getShareUrl()}
                          class="flex-1 rounded-lg border border-primary-border bg-panel px-3 py-2 text-sm text-primary-text"
                        />
                        <StButton onClick={() => void handleCopyLink()} class="rounded-lg px-3 py-2">
                          <Show when={copied()} fallback={<TbCopy class="size-4" />}>
                            <TbCheck class="size-4" />
                          </Show>
                        </StButton>
                      </div>

                      <Show when={copied()}>
                        <div class="flex items-center justify-center gap-2">
                          <TbCheck class="size-4 shrink-0 text-green-600 dark:text-green-400" />
                          <p class="text-sm text-green-600 dark:text-green-400">{t("chat_share_copied")}</p>
                        </div>
                      </Show>

                      {/* Copyable text block — shown when user selected text before sharing, or when caller supplied a textBlock (e.g. chat title for whole-page share). */}
                      <Show when={displayedTextBlock()}>
                        <div class="space-y-2 border-t border-primary-border pt-2">
                          <p class="text-sm font-bold text-primary-text">{t("chat_share_copy_text_block")}</p>
                          <div class="flex items-start gap-2">
                            <div class="max-h-32 flex-1 cursor-default overflow-y-auto rounded-lg border border-primary-border bg-panel px-3 py-2 text-sm wrap-break-word text-primary-text underline">
                              {displayedTextBlock()}
                            </div>
                            <StButton
                              onClick={() => void handleCopyTextBlock()}
                              class="mt-0.5 shrink-0 rounded-lg px-3 py-2"
                            >
                              <Show when={textBlockCopied()} fallback={<TbCopy class="size-4" />}>
                                <TbCheck class="size-4" />
                              </Show>
                            </StButton>
                          </div>
                          <Show when={textBlockCopied()}>
                            <div class="flex items-center justify-center gap-2">
                              <TbCheck class="size-4 shrink-0 text-green-600 dark:text-green-400" />
                              <p class="text-sm text-green-600 dark:text-green-400">
                                {t("chat_share_text_block_copied")}
                              </p>
                            </div>
                          </Show>
                        </div>
                      </Show>

                      {/* Every heading of the shared answer, as a nested list of section links. */}
                      <Show when={outlineHeadings().length > 0}>
                        <div class="space-y-2 border-t border-primary-border pt-2">
                          <p class="text-sm font-bold text-primary-text">{t("chat_share.copy_headers")}</p>
                          <div class="flex items-start gap-2">
                            <div class="max-h-32 flex-1 cursor-default overflow-y-auto rounded-lg border border-primary-border bg-panel px-3 py-2 text-sm text-primary-text">
                              <For each={previewRows()}>
                                {(row) => (
                                  <p class="truncate underline" style={{ "padding-left": `${(row.depth - 1) * 12}px` }}>
                                    {row.text}
                                  </p>
                                )}
                              </For>
                            </div>
                            <StButton
                              onClick={() => void handleCopyOutline()}
                              class="mt-0.5 shrink-0 rounded-lg px-3 py-2"
                            >
                              <Show when={outlineCopied()} fallback={<TbCopy class="size-4" />}>
                                <TbCheck class="size-4" />
                              </Show>
                            </StButton>
                          </div>
                          <Show when={outlineCopied()}>
                            <div class="flex items-center justify-center gap-2">
                              <TbCheck class="size-4 shrink-0 text-green-600 dark:text-green-400" />
                              <p class="text-sm text-green-600 dark:text-green-400">{t("chat_share.headers_copied")}</p>
                            </div>
                          </Show>
                        </div>
                      </Show>

                      <div class="border-t border-primary-border pt-2">
                        <StButton
                          onClick={() => setShowEmailSection(!showEmailSection())}
                          class="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-primary-text"
                        >
                          <TbMail class="size-4" />
                          {t("chat_share_email_title")}
                        </StButton>

                        <Show when={showEmailSection()}>
                          <div class="mt-3 space-y-3">
                            <EmailTagsInput
                              emails={emails}
                              onEmailsChange={setEmails}
                              maxEmails={MAX_EMAILS}
                              placeholder={t("chat_share_email_placeholder")}
                              error={emailError}
                              onError={setEmailError}
                            />

                            <Show when={emailSuccess()}>
                              <p class="text-sm text-green-600 dark:text-green-400">{emailSuccess()}</p>
                            </Show>

                            <StButton
                              noColor
                              onClick={() => void handleSendEmails()}
                              disabled={isSendingEmails() || emails().length === 0}
                              class={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 transition-colors disabled:opacity-50 ${emails().length > 0 && !isSendingEmails() ? "bg-brand text-on-brand hover:bg-brand-hover" : "bg-primary text-primary-text"}`}
                            >
                              <Show when={isSendingEmails()} fallback={<>{t("chat_share_email_send")}</>}>
                                <TbLoader2 class="size-4 animate-spin" />
                                {t("chat_share_email_sending")}
                              </Show>
                            </StButton>
                          </div>
                        </Show>
                      </div>

                      <div class="border-t border-primary-border pt-2">
                        <StButton
                          onClick={() => void handleRevokeLink()}
                          disabled={isRevoking()}
                          class="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/20"
                        >
                          <Show when={isRevoking()} fallback={<TbLinkOff class="size-4" />}>
                            <TbLoader2 class="size-4 animate-spin" />
                          </Show>
                          {t("chat_share_revoke")}
                        </StButton>
                      </div>

                      <Show when={props.promptShareUrl}>
                        <div class="space-y-2 border-t border-primary-border pt-2">
                          <p class="text-sm font-bold text-primary-text">{t("chat_share_also_prompt_title")}</p>
                          <p class="text-xs text-secondary-text">{t("chat_share_also_prompt_description")}</p>
                          <div class="flex items-center gap-2">
                            <input
                              type="text"
                              readonly
                              value={props.promptShareUrl ?? ""}
                              class="flex-1 rounded-lg border border-primary-border bg-panel px-3 py-2 text-sm text-primary-text"
                            />
                            <StButton onClick={() => void handleCopyPromptLink()} class="rounded-lg px-3 py-2">
                              <Show when={promptLinkCopied()} fallback={<TbCopy class="size-4" />}>
                                <TbCheck class="size-4" />
                              </Show>
                            </StButton>
                          </div>
                        </div>
                      </Show>
                    </div>
                  </Show>
                </Show>

                <div class="mt-6 flex items-center justify-end gap-3">
                  <StButton onClick={props.onClose} class="px-4 py-2 text-secondary-text hover:text-primary-text">
                    {t("common.close")}
                  </StButton>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
